import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  ArrowClockwise,
  ArrowSquareOut,
  Buildings,
} from "@phosphor-icons/react";
import { TopNav } from "@/components/gbp/TopNav";
import { getScanHistory } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function relative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const hours = diffMs / 3600000;
  if (hours < 1) return `${Math.max(1, Math.round(diffMs / 60000))}m ago`;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDoc = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startToday - startDoc) / 86400000);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return "";
}

const STATUS_TONE = {
  completed: "bg-[#E6F4EA] text-[#0C6B2E] border-[#A3E0B5]",
  failed: "bg-[#FCE8E8] text-[#C92A2A] border-[#FECACA]",
  fetching: "bg-[#E8ECFF] text-[#002DFF] border-[#B8C4FF]",
  discovering: "bg-[#E8ECFF] text-[#002DFF] border-[#B8C4FF]",
  pending: "bg-[#F1F3F5] text-[#495057] border-[#DEE2E6]",
};

function groupByDay(items) {
  const groups = new Map();
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (const it of items) {
    const d = new Date(it.created_at);
    const startDoc = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const daysBack = Math.round((startToday - startDoc) / 86400000);
    let label;
    if (daysBack === 0) label = "Today";
    else if (daysBack === 1) label = "Yesterday";
    else if (daysBack < 7) label = `${daysBack} days ago`;
    else {
      label = d.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(it);
  }
  return Array.from(groups.entries());
}

export default function History() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getScanHistory(200);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Could not load scan history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter(
      (it) =>
        (it.niche || "").toLowerCase().includes(needle) ||
        (it.area || "").toLowerCase().includes(needle),
    );
  }, [items, q]);

  const grouped = useMemo(() => groupByDay(filtered), [filtered]);

  const openScan = (id) => {
    navigate(`/?scan=${encodeURIComponent(id)}`);
  };

  return (
    <div data-testid="history-page" className="min-h-screen bg-white">
      <TopNav />
      <main className="mx-auto max-w-6xl px-6 pb-24 pt-8 md:px-10">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-xs font-medium text-ink">
              All scans
            </div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Scan history
            </h1>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              Every scan you&apos;ve ever run. {total > 0 ? `${total} total.` : ""}
            </p>
          </div>
          <button
            type="button"
            data-testid="history-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 font-sans text-sm text-ink transition-colors hover:border-electric hover:text-electric"
          >
            <ArrowClockwise size={14} weight="bold" />
            Refresh
          </button>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 transition-colors focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/30">
            <MagnifyingGlass size={16} weight="bold" className="text-muted-foreground" />
            <input
              data-testid="history-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by niche or area…"
              className="w-full bg-transparent font-sans text-sm text-ink placeholder:text-muted-foreground focus:outline-none"
            />
            {q ? (
              <button
                type="button"
                onClick={() => setQ("")}
                className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-electric"
              >
                clear
              </button>
            ) : null}
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 rounded-lg gbp-shimmer" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="history-empty"
            className="rounded-lg border border-dashed border-border bg-surface px-6 py-16 text-center"
          >
            <div className="font-display text-lg font-semibold text-ink">
              No scans match that filter
            </div>
            <div className="mt-1 font-sans text-sm text-muted-foreground">
              {q
                ? "Try a shorter search term."
                : "Run your first scan from the Home page and it will appear here."}
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map(([dayLabel, list]) => (
              <section
                key={dayLabel}
                data-testid={`history-group-${dayLabel.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="font-display text-lg font-semibold text-ink">
                    {dayLabel}
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    {list.length} {list.length === 1 ? "scan" : "scans"}
                  </div>
                </div>
                <div className="overflow-hidden rounded-lg border border-border bg-white">
                  <table className="w-full font-sans text-sm">
                    <thead>
                      <tr className="border-b border-border bg-surface/60">
                        <th className="px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                          Niche
                        </th>
                        <th className="px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                          Area
                        </th>
                        <th className="px-3 py-2 text-right font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                          Businesses
                        </th>
                        <th className="px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                          When
                        </th>
                        <th className="px-3 py-2 text-right"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((it) => (
                        <tr
                          key={it.id}
                          data-testid={`history-row-${it.id}`}
                          onClick={() => openScan(it.id)}
                          className="cursor-pointer border-b border-border/60 transition-colors last:border-b-0 hover:bg-electric-soft/30"
                        >
                          <td className="px-3 py-2.5 font-sans font-semibold text-ink">
                            {it.niche}
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">
                            {it.area}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <span className="inline-flex items-center gap-1 font-mono tabular-nums text-ink">
                              <Buildings size={12} weight="bold" className="text-muted-foreground" />
                              {it.total ?? 0}
                              {it.cap && it.cap !== (it.total ?? 0) ? (
                                <span className="font-mono text-[10px] text-muted-foreground">
                                  /{it.cap}
                                </span>
                              ) : null}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                                STATUS_TONE[it.status] || STATUS_TONE.pending
                              }`}
                            >
                              {it.status}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">
                            <div>{formatDate(it.created_at)}</div>
                            {relative(it.created_at) ? (
                              <div className="font-mono text-[10px] uppercase tracking-wider">
                                {relative(it.created_at)}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <ArrowSquareOut
                              size={14}
                              weight="bold"
                              className="text-muted-foreground"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
